/*
  Cart drawer (snippets/cart-drawer.liquid): the receipt in a modal <dialog>. A bottom sheet on phones,
  a floating panel on the right from 750px, both on the dark tray (assets/cart-drawer.css). The toast
  and the slip's print show at every width since desktop-01.

  - Opens after `add;` and quick add (assets/product-form.js, assets/product-card.js: they ask
    /cart/add for getSectionsToRender(), then call setActiveElement() and renderContents()), from the
    header's `cart;N` (a plain link to /cart without JavaScript) and from Shopify's Standard Actions.
    On phones an add shows the added toast instead (snippets/cart-toast.liquid says how); opening the
    drawer hides it.
  - Opens and closes through Overlays (assets/global.js, docs/UI-MAP.md 3.7): the page behind is inert
    and doesn't scroll, Escape closes it, focus goes back to the opener. The browser's own modal, not a
    strict trap: focus visits the browser's toolbar once per loop (a lead decision; it passes WCAG).
  - `close` and the backdrop close it, but a press counts on the backdrop only when it starts and ends
    there (QA G07), and no press counts right after opening (QA G15: a double tap on `cart;N`). A
    keyboard `close` always counts.
  - The slide is a CSS transition (none under reduced motion); closing waits for it, opening again
    during it cancels the close.
  - Focus moves to `close` when it opens (the final design, d-49); Tab then goes through each line's
    `remove`, `−`, `+`, then `pay;`.
  - Each open stamps the receipt with the time now (QA C17). On phones the slip then prints down
    (translateY -101% to 0, 1100ms in 18 steps after 250ms), on open only (never when assets/cart.js
    swaps in a fresh receipt), not under reduced motion.
*/
if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      // A press this soon after opening is a double tap's second tap.
      static DOUBLE_TAP_MS = 500;
      // The pause before the slip prints in.
      static PRINT_DELAY_MS = 250;

      constructor() {
        super();

        this.dialog = this.querySelector('dialog');
        // Every width since desktop-01 (the name is from when the toast and the print were phones only).
        this.phone = window.matchMedia('screen');
        this.toast = document.getElementById('CartToast');
        this.openedAt = -Infinity;
        this.dialog.addEventListener('pointerdown', (event) => {
          this.press = {
            onBackdrop: this.isBackdrop(event),
            early: event.timeStamp - this.openedAt < CartDrawer.DOUBLE_TAP_MS,
          };
        });
        this.dialog.addEventListener('click', this.onClick.bind(this));
        this.dialog.addEventListener('close', this.onClose.bind(this));
        // Focus moving into the slip while it prints lands it at once, before the receipt scrolls to it.
        this.dialog.addEventListener(
          'focusin',
          (event) => event.target.closest('.receipt__paper') && this.printing?.finish(),
          true
        );

        const cartLink = document.getElementById('cart-icon-bubble');
        if (!cartLink) return;
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
        cartLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.openSoon(cartLink);
        });
        cartLink.addEventListener('keydown', (event) => {
          if (event.key !== ' ') return;
          event.preventDefault();
          this.openSoon(cartLink);
        });
      }

      // A tap on `cart;N`: the tap paints first, the drawer opens after (afterPaint, d-40); a second
      // tap before then does nothing.
      openSoon(opener) {
        if (this.opening) return;
        this.opening = true;
        afterPaint(() => {
          this.opening = false;
          this.open(opener);
        });
      }

      // The sections an add to cart asks for; renderContents() puts them in place. Every add asks as it
      // starts, so on phones the toast's files start loading here, with the request.
      getSectionsToRender() {
        if (this.usesToast()) this.loadToast().catch(() => {});
        return [{ id: 'cart-drawer' }, { id: 'cart-icon-bubble' }, { id: 'cart-live-region-text' }];
      }

      // Where focus returns when the drawer closes. `detail`: for the phone toast, when the add then
      // reaches renderContents() through QuickAddCart.handOver() (assets/size-sheet.js); only for `element`.
      setActiveElement(element, detail) {
        this.activeElement = element;
        if (detail) this.addDetail = { element, detail };
      }

      // From 750px the drawer opens; on phones the toast shows instead, if it loads.
      renderContents(response, detail) {
        renderCartSections(response.sections);
        const opener = this.activeElement;
        detail = { ...(this.addDetail?.element === opener && this.addDetail.detail), ...detail, opener };
        this.addDetail = null;
        if (!this.usesToast()) return this.open();
        this.activeElement = null;
        this.loadToast()
          .then((toast) => toast.added(response, detail))
          .catch((error) => {
            if (error) console.error(error);
            this.open(opener);
          });
      }

      // A failed add's message in the toast (phones), for an add with nowhere else to show it. False
      // from 750px: the caller shows it.
      renderError(message, detail) {
        if (!this.usesToast()) return false;
        this.loadToast()
          .then((toast) => toast.failed(message, detail))
          .catch(() => announce(message));
        return true;
      }

      usesToast() {
        return Boolean(this.toast) && this.phone.matches;
      }

      // The toast's styles and script (named on it), once; after a failure, again next time.
      loadToast() {
        const load = (element) =>
          new Promise((resolve, reject) => {
            element.onload = resolve;
            element.onerror = () => reject(element.remove());
            document.body.append(element);
          });
        const { stylesheet, script } = this.toast.dataset;
        if (!this.toastReady) {
          this.toastReady = Promise.all([
            load(Object.assign(document.createElement('link'), { rel: 'stylesheet', href: stylesheet })),
            load(Object.assign(document.createElement('script'), { src: script })),
          ]).then(() => this.toast);
          this.toastReady.catch(() => (this.toastReady = null));
        }
        return this.toastReady;
      }

      // On the backdrop: the press lands on the dialog itself, outside its box (the panel fills it).
      isBackdrop(event) {
        if (event.target !== this.dialog) return false;
        const box = this.dialog.getBoundingClientRect();
        const { clientX: x, clientY: y } = event;
        return x < box.left || x > box.right || y < box.top || y > box.bottom;
      }

      onClick(event) {
        // A keyboard press (detail 0) has no pointerdown of its own.
        const press = event.detail ? this.press : null;
        this.press = null;
        if (press?.early) return;
        if (event.target.closest('[data-cart-drawer-close]')) this.close();
        else if (press?.onBackdrop && this.isBackdrop(event)) this.close();
      }

      open(opener) {
        const returnTo = opener || this.activeElement || document.activeElement;
        this.activeElement = null;
        if (this.dialog.open) {
          if (!this.closing) return;
          clearTimeout(this.closeTimer);
          this.closing = false;
          Overlays.open(this.dialog);
          this.dialog.classList.add('is-open');
          return;
        }

        this.openedAt = performance.now();
        Overlays.open(this.dialog, { name: 'cart', opener: returnTo, hide: () => this.slideOut() });
        const receipt = this.querySelector('cart-items');
        receipt?.stamp?.();
        // Laid out off screen first, so adding the class slides it in.
        this.dialog.getBoundingClientRect();
        this.dialog.classList.add('is-open');
        // Focus goes to `close` (the final design, d-49), else the dialog itself.
        const close = this.dialog.querySelector('[data-cart-drawer-close]');
        if (close) close.focus();
        else this.dialog.focus();
        this.printIn();
        receipt?.dispatchViewEvent?.();
        // Opening it hides the added toast at once (after focus moved in, so the toast gives none back).
        this.toast?.hide?.();
      }

      close() {
        Overlays.close(this.dialog);
      }

      // Overlays closes it this way: the slide-out, then the dialog closes (at once under reduced motion).
      slideOut() {
        if (this.closing) return;
        this.closing = true;
        this.dialog.classList.remove('is-open');
        const durations = getComputedStyle(this.dialog).transitionDuration.split(',').map(parseFloat);
        this.closeTimer = setTimeout(() => this.dialog.close(), (Math.max(...durations) || 0) * 1000);
      }

      // Phones: the slip feeds down from under the header in steps (--dur-print, --ease-print), clipped
      // by its tray (.receipt__tray); `pay;` shows from the start. Web animations ignore the global
      // reduced-motion rule, hence the check.
      printIn() {
        const paper = this.querySelector('.receipt__paper');
        if (!this.phone.matches || reducedMotion() || !paper?.animate) return;
        const root = getComputedStyle(document.documentElement);
        this.printing = paper.animate(
          { transform: ['translateY(-101%)', 'none'] },
          {
            duration: cssTime(root.getPropertyValue('--dur-print'), 1100),
            delay: CartDrawer.PRINT_DELAY_MS,
            easing: root.getPropertyValue('--ease-print').trim() || 'steps(18, end)',
            fill: 'backwards',
          }
        );
      }

      // However it closed (Overlays gives the scroll and focus back).
      onClose() {
        clearTimeout(this.closeTimer);
        this.closing = false;
        this.press = null;
        this.dialog.classList.remove('is-open');
        this.printing?.cancel();
        this.printing = null;
      }
    }
  );
}
